// ContributorLandingPage.jsx — /invite/:token
// v1.11 — Consent PR E2 (Oct 4, 2026; spec PR-E v0.2 §2 E2; plan v0.26 §9a D18, copy v0.3 §5): when the
//   server says contributorRelationshipCaptureEnabled (GET /invite/:token, PR C), the claim screen asks
//   RL-1 "Your name" (pre-filled with what the inviter typed, editable; helper RL-2) and RL-3 "Who {name}
//   is to you (optional)" from the ONE shared list (src/constants/relationships, 14 entries); "Someone
//   else…" reveals RL-6 "In your own words" (≤160). Order on the one screen: name → relationship → consent.
//   The answers survive the email-code redirect the same way the consent tick does: the page-local
//   sessionStorage entry now holds JSON { affirmed, contributorName, relationship, relationshipDetail }
//   (legacy value '1' still reads as affirmed), and they go on POST /me/contributions/link
//   (contributorName / relationship / relationshipDetail — server validates, stores in the link
//   transaction, ignores them when its flag is off). Flag false/absent => screen byte-identical to v1.10.
// v1.10 — NS-61 Phase 2: handleConnect now transmits the affirmative consent the contributor
//   already gave (the "I understand" tick — `agreed` for a signed-in Continue, or the sessionStorage
//   consent flag on the return path) as consent:{affirmed:true} on POST /me/contributions/link, so
//   the server (anamoria-contributors v1.23) can verify + record it against the correct notice.
//   No UX change — the checkbox and copy are unchanged; we only send the result. Omits the object
//   if affirmation is somehow absent (server transitional path, not the Phase 3 required-gate).
// v1.9 — Copy v0.7 (thanatologist-reviewed): Type-1 consent simplified to "When you add a
//   memory, it will be visible to {inviter} and the others they've chosen to include…" (preamble
//   dropped); NEW Type-2 variant for a grief-group-leader space ("Each memory you add here is
//   yours. You can keep it just for you, or decide who can see it. Nothing is shared unless you
//   choose to…"). Variant switches on the INVITER's group-leader flag (backend
//   inviterIsGroupLeader = users.pilot_role === 'leader'; Sophie 2026-08-08) — this is the O-i
//   consent-integrity switch. O-g: inviter name is position-aware (backend now returns null when
//   absent; "A friend" at sentence start, "the person who invited you" mid-sentence). Still open
//   pre-enablement: O-b/O-j (per-type policy_documents row/version + docType), O-f (terms-owner
//   glance now covers the Type-1 wording change + Type-2), O-i test per type, O-k (confirm Type-1
//   has no keep-private), O-a. Copy: Anamoria_Contributor_SignIn_Claim_Copy_for_Thanatologist_v0.7.
// v1.8 — NEW-9 Track B (B-1): the COMBINED one-screen claim. Supersedes the rejected
//   two-screen render (v1.7). One screen carries the invitation, the distinct contributor
//   consent (canonical §5, restructured — copy v0.5), an affirmative "I understand" checkbox
//   that REVEALS the email + "Send my code", and the private-space note (inside the reveal).
//   Flow:
//     - Not signed in: read the consent, tick "I understand" (unchecked by default; the only
//       way forward), enter email, "Send my code" → we set a short-lived, page-local consent
//       flag (sessionStorage `ana_contribConsent:<token>`; option A) and call
//       loginWithRedirect({ authorizationParams: { login_hint }, appState: { returnTo } }).
//       The `login_hint` is what lets the branded ACUL screen skip email entry (NS-38, ACUL
//       repo — additive; harmless here if not yet shipped, the contributor just sees the
//       prefilled email screen).
//     - On return (signed in) with the flag present: we auto-run the link
//       (POST /me/contributions/link) — consent was ticked before the redirect — clear the
//       flag, and go to /contributing/:spaceId/memories. The contributor does NOT see the
//       form again.
//     - Signed-in visitor with NO flag (direct visit / existing account): show the same
//       consent screen; ticking "I understand" reveals a "Continue" button that links
//       directly (no code needed).
//   Copy: contributor consent per copy v0.5 (§5 restructured). Inviter name from
//     invite.invitedByName (backend handleValidateToken v1.18; "A friend" fallback here too).
//   NOT resolved here (pre-enablement gates, tracked): O-b — the consent still records against
//     the member bundle (/privacy) until it gets its own policy_documents row + URL + the
//     one-line backend docType change; O-a — {spaceName} possessive render; O-f — terms-owner
//     glance on the restructured consent block.
//   The anonymous claim (email + POST /invite/:token/claim → ana_sessionToken) stays retired
//     (v1.7); no ana_sessionToken is read or written anywhere.
//
// Prior history (v1.0–v1.7): anonymous claim → session token (retired v1.7); F3 account-link
//   card; affirmative consent gate; Privacy Policy link; email verification on claim;
//   WCAG 2.2 AA (aria-label on primary action, role="alert"/aria-live on errors).
//   The v1.7 two-screen render (invite "Continue" → Auth0 → post-sign-in "You're all set" +
//   consent) was rejected live 2026-08-08 and is replaced by this combined screen.
// APIs: GET /invite/:token (public via API key) — now returns invitedByName;
//       POST /me/contributions/link (JWT) — the link step.

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import config from '../config';
import { createApiClient } from '../api/client';
import styles from './ContributorLandingPage.module.css';
import { RELATIONSHIP_OPTIONS } from '../constants/relationships'; // E2: shared 14-entry list

// Direct fetch for public routes (no JWT, uses API key)
async function publicFetch(path, options = {}) {
  const url = `${config.apiUrl}${path}`;
  const res = await fetch(url, {
    ...options,
    mode: 'cors',
    headers: { 'Content-Type': 'application/json', 'x-api-key': config.apiKey, ...(options.headers || {}) },
  });
  const data = res.headers.get('content-type')?.includes('json') ? await res.json() : {};
  if (!res.ok) throw { error: data.error || 'REQUEST_FAILED', status: res.status };
  return data;
}

const EMAIL_RE = /\S+@\S+\.\S+/;
const RELATIONSHIP_DETAIL_MAX = 160;

// E2: the page-local consent entry. v1.8–v1.10 stored the string '1'; v1.11 stores JSON so the name and
// relationship typed before the redirect come back with the affirmation. Both shapes read.
function readConsentEntry(key) {
  try {
    const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null;
    if (!raw) return null;
    if (raw === '1') return { affirmed: true };
    const parsed = JSON.parse(raw);
    return parsed && parsed.affirmed === true ? parsed : null;
  } catch { return null; }
}

export default function ContributorLandingPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Combined-screen state
  const [agreed, setAgreed] = useState(false);   // "I understand" — unchecked by default
  const [email, setEmail] = useState('');
  // E2: name + relationship (only rendered/sent when the server flag is on)
  const [contributorName, setContributorName] = useState('');
  const [relationship, setRelationship] = useState('');          // '' = not answered (optional)
  const [relationshipDetail, setRelationshipDetail] = useState('');

  // Link (return / authenticated) state
  const [linking, setLinking] = useState(false);
  const [connectError, setConnectError] = useState(null);
  const [ownSpaceId, setOwnSpaceId] = useState(null);
  const autoTriedRef = useRef(false);

  // Option A: a short-lived, page-local flag that survives the same-tab Auth0 redirect,
  // signalling on return that consent was given so the link can auto-complete. NOT a session
  // token; cleared as soon as the link resolves.
  const consentKey = `ana_contribConsent:${token}`;
  const storedConsent = readConsentEntry(consentKey);   // E2: JSON entry (or legacy '1')
  const flagPresent = storedConsent !== null;

  const emailValid = EMAIL_RE.test(email.trim());
  // Inviter name: backend returns null when the inviter has no display_name. Position-aware
  // fallback (O-g): "A friend has invited you" reads fine at sentence start, but "visible to
  // A friend" mid-sentence does not — use "the person who invited you" there.
  const inviterFirst = invite?.invitedByName || 'A friend';
  const inviterMid = invite?.invitedByName || 'the person who invited you';
  // Consent variant (copy v0.7, O-i): Type-2 for a grief-group-leader space, Type-1 for a
  // member space. Discriminator = the inviter's group-leader flag (backend inviterIsGroupLeader
  // from users.pilot_role === 'leader'). Non-leader / unknown => Type-1 (member).
  const isGroupLeaderSpace = invite?.inviterIsGroupLeader === true;
  // E2: does the claim screen ask name + relationship? Server-resolved from the inviter's group (PR C).
  const askProfile = invite?.contributorRelationshipCaptureEnabled === true;

  // Validate token on mount
  useEffect(() => {
    let cancelled = false;
    async function validate() {
      try {
        const data = await publicFetch(`/invite/${token}`, { method: 'GET' });
        if (!cancelled) { setInvite(data); setContributorName((n) => n || data?.contributorName || ''); }
      } catch (err) {
        if (err.error === 'INVALID_TOKEN') {
          setError('This invite link is not valid.');
        } else if (err.error === 'TOKEN_ALREADY_USED') {
          setError('This invite has already been used.');
        } else {
          setError('Something went wrong. Please try the link again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    validate();
    return () => { cancelled = true; };
  }, [token]);

  // Link this invite to the signed-in account (POST /me/contributions/link). Used both by the
  // auto-link on return (flag present) and by the direct "Continue" for a signed-in visitor.
  const handleConnect = useCallback(async () => {
    setLinking(true);
    setConnectError(null);
    try {
      const api = createApiClient(getAccessTokenSilently);
      // NS-61 Phase 2: transmit the affirmative consent the contributor already gave (the
      // "I understand" tick) so the server can verify + record it. It is held as `agreed` for a
      // signed-in visitor clicking Continue, or as the sessionStorage consent flag set before the
      // OTP redirect on the return path. handleConnect is only reached after affirmation, so this
      // is normally true; if somehow absent we omit the object so the server's transitional path
      // (not the Phase 3 required-gate) handles it rather than 400-ing the claim.
      const stored = readConsentEntry(consentKey);
      const consentAffirmed = agreed || stored !== null;
      const linkPayload = { inviteToken: token };
      if (consentAffirmed) linkPayload.consent = { affirmed: true };
      // E2: name / relationship — from the stored entry on the return path, else from the form (signed-in
      // Continue). Sent only when the server asked for them; absent fields are simply not sent (optional).
      if (askProfile) {
        // Return path (stored entry present): the person never saw the form again, so ONLY what was stored
        // before the redirect is sent (a legacy '1' entry carries nothing). Signed-in Continue: the form.
        const src = stored !== null ? stored : { contributorName, relationship, relationshipDetail };
        const name = (src.contributorName || '').trim();
        if (name) linkPayload.contributorName = name;
        if (src.relationship) {
          linkPayload.relationship = src.relationship;
          if (src.relationship === 'other' && (src.relationshipDetail || '').trim()) {
            linkPayload.relationshipDetail = src.relationshipDetail.trim().slice(0, RELATIONSHIP_DETAIL_MAX);
          }
        }
      }
      const data = await api.post('/me/contributions/link', linkPayload);
      try { sessionStorage.removeItem(consentKey); } catch { /* ignore */ }
      navigate(`/contributing/${data.spaceId}/memories`, { replace: true });
    } catch (err) {
      if (err.error === 'OWN_SPACE') {
        try { sessionStorage.removeItem(consentKey); } catch { /* ignore */ }
        setOwnSpaceId(err.spaceId || null);
      } else if (err.error === 'TOKEN_EXPIRED' || err.error === 'TOKEN_REVOKED') {
        setConnectError('This invitation has expired. Ask the person who invited you to send a new one.');
      } else if (err.error === 'TOO_MANY_ATTEMPTS') {
        setConnectError('Please try again in a little while.');
      } else {
        setConnectError("We weren't able to open this space right now. Please try again, or ask the person who invited you to send a new link.");
      }
      setLinking(false);
    }
  }, [getAccessTokenSilently, token, navigate, consentKey, agreed, askProfile, contributorName, relationship, relationshipDetail]);

  // Auto-link on return: signed in, consent flag present, not our own space, once.
  useEffect(() => {
    if (!invite || authLoading) return;
    if (isAuthenticated && flagPresent && !ownSpaceId && !autoTriedRef.current) {
      autoTriedRef.current = true;
      handleConnect();
    }
  }, [invite, authLoading, isAuthenticated, flagPresent, ownSpaceId, handleConnect]);

  // Not signed in: tick consent → enter email → send the code (login_hint + returnTo).
  const handleSendCode = useCallback(() => {
    if (!emailValid) return;
    // E2: carry the answers across the redirect with the affirmation (JSON; was '1').
    const entry = { affirmed: true };
    if (askProfile) {
      entry.contributorName = contributorName.trim();
      entry.relationship = relationship || '';
      entry.relationshipDetail = relationship === 'other' ? relationshipDetail.trim().slice(0, RELATIONSHIP_DETAIL_MAX) : '';
    }
    try { sessionStorage.setItem(consentKey, JSON.stringify(entry)); } catch { /* ignore */ }
    loginWithRedirect({
      authorizationParams: { login_hint: email.trim() },
      appState: { returnTo: `/invite/${token}` },
    });
  }, [emailValid, email, loginWithRedirect, token, consentKey, askProfile, contributorName, relationship, relationshipDetail]);

  if (loading || authLoading) {
    return <div className={styles.loading}><div className={styles.loadingDot} /><span>Loading invite...</span></div>;
  }

  if (error && !invite) {
    return (
      <div className={styles.screen}>
        <div className={styles.card}>
          <span className={styles.logo}>Anamoria</span>
          <div className={styles.errorIcon}>⚠️</div>
          <p className={styles.errorText}>{error}</p>
        </div>
      </div>
    );
  }

  // The invite is for a space the signed-in user owns.
  const ownSpaceView = isAuthenticated && ownSpaceId;
  // Returning from the code screen (or otherwise mid-link) — auto-link is running/handled.
  const returningView = isAuthenticated && flagPresent && !ownSpaceId;

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <span className={styles.logo}>Anamoria</span>
        <h1 className={styles.title}>You&apos;ve been invited</h1>
        <p className={styles.subtitle}>
          to add a memory to <strong>{invite?.spaceName}</strong>&apos;s space
        </p>

        {ownSpaceView && (
          <div className={styles.connectBox}>
            <p className={styles.connectText}>This space is already yours.</p>
            <button
              className={styles.btnPrimary}
              onClick={() => navigate(`/spaces/${ownSpaceId}`, { replace: true })}
            >
              Go to your space
            </button>
          </div>
        )}

        {/* Return path: consent already given before the redirect; the link runs automatically.
            The contributor does not see the form again. */}
        {!ownSpaceView && returningView && (
          <div className={styles.connectBox}>
            {connectError ? (
              <>
                <div className={styles.errorBanner} role="alert" aria-live="polite">{connectError}</div>
                <button className={styles.btnPrimary} onClick={handleConnect} disabled={linking}>
                  {linking ? 'One moment…' : 'Try again'}
                </button>
              </>
            ) : (
              <p className={styles.connectText}>One moment…</p>
            )}
          </div>
        )}

        {/* Combined consent screen: not signed in, or a signed-in visitor with no pending
            consent flag (direct visit). */}
        {!ownSpaceView && !returningView && (
          <>
            <p className={styles.greeting}>Hi {invite?.contributorName},</p>
            <p className={styles.bodyText}>
              {inviterFirst} has invited you to add memories of {invite?.spaceName} to a private space on Anamoria.
            </p>
            {isGroupLeaderSpace ? (
              <p className={styles.bodyText}>
                Each memory you add here is yours. You can keep it just for you, or decide who can see it. Nothing is shared unless you choose to. It will not be public, and we will not use it for anything else.
              </p>
            ) : (
              <p className={styles.bodyText}>
                When you add a memory, it will be visible to {inviterMid} and the others they&apos;ve chosen to include. It will not be public, and we will not use it for anything else.
              </p>
            )}
            <p className={styles.bodyTextLast}>
              You can add a memory whenever you&apos;re ready, and you can stop at any time.
            </p>

            {/* E2: name → who {name} is to you → consent (D18). Rendered only when the server asks. */}
            {askProfile && (
              <div className={styles.emailField} data-e2-profile>
                <label className={styles.emailLabel} htmlFor="contributor-name">Your name</label>
                <input
                  id="contributor-name"
                  type="text"
                  className={styles.emailInput}
                  value={contributorName}
                  onChange={(e) => setContributorName(e.target.value)}
                  maxLength={100}
                  autoComplete="name"
                />
                <p className={styles.bodyText} style={{ marginTop: 6 }}>When you add a memory, it shows your name.</p>
                <label className={styles.emailLabel} htmlFor="contributor-relationship" style={{ marginTop: 12 }}>
                  Who {invite?.spaceName} is to you (optional)
                </label>
                <select
                  id="contributor-relationship"
                  className={styles.emailInput}
                  value={relationship}
                  onChange={(e) => { setRelationship(e.target.value); if (e.target.value !== 'other') setRelationshipDetail(''); }}
                >
                  <option value="">Choose one, or leave it</option>
                  {RELATIONSHIP_OPTIONS.map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
                {relationship === 'other' && (
                  <>
                    <label className={styles.emailLabel} htmlFor="contributor-relationship-detail" style={{ marginTop: 12 }}>In your own words</label>
                    <input
                      id="contributor-relationship-detail"
                      type="text"
                      className={styles.emailInput}
                      value={relationshipDetail}
                      onChange={(e) => setRelationshipDetail(e.target.value)}
                      maxLength={RELATIONSHIP_DETAIL_MAX}
                    />
                  </>
                )}
              </div>
            )}

            <label className={styles.consentCheckboxLabel}>
              <input
                type="checkbox"
                className={styles.consentCheckbox}
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <span className={styles.consentCheckboxText}>I understand</span>
            </label>

            {agreed && !isAuthenticated && (
              <div className={styles.revealSection}>
                <p className={styles.bodyText}>
                  When you&apos;re ready, add your email and we&apos;ll send you a code, so we know it&apos;s you.
                </p>
                <div className={styles.emailField}>
                  <label className={styles.emailLabel} htmlFor="contributor-email">Your email</label>
                  <input
                    id="contributor-email"
                    type="email"
                    className={styles.emailInput}
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    inputMode="email"
                  />
                </div>
                <button
                  className={styles.btnPrimary}
                  onClick={handleSendCode}
                  disabled={!emailValid}
                  aria-label="Send my code"
                >
                  Send my code
                </button>
                <div className={styles.privacyNote}>
                  <span aria-hidden="true">🔒</span>
                  <span>This is a private space. Only invited people can see what&apos;s shared here.</span>
                </div>
              </div>
            )}

            {agreed && isAuthenticated && (
              <div className={styles.revealSection}>
                {connectError && (
                  <div className={styles.errorBanner} role="alert" aria-live="polite">{connectError}</div>
                )}
                <button
                  className={styles.btnPrimary}
                  onClick={handleConnect}
                  disabled={linking}
                  aria-label={`Add memories to ${invite?.spaceName || 'this'}'s space`}
                >
                  {linking ? 'One moment…' : 'Continue'}
                </button>
                <div className={styles.privacyNote}>
                  <span aria-hidden="true">🔒</span>
                  <span>This is a private space. Only invited people can see what&apos;s shared here.</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
